import React from "react";
import SectionCard from "./SectionCard";
import { Badge } from "components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "components/ui/tooltip";

const formatDate = (dateString) => {
  if (!dateString) return "";
  return new Date(dateString).toLocaleDateString("en-US", { month: "short", year: "numeric" });
};

const formatCurrency = (amount) => {
  if (amount == null) return "N/A";
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: "compact", maximumFractionDigits: 1 }).format(amount);
};

const getGrantStatus = (endDateString) => {
  if (!endDateString) return { label: "Active", className: "bg-green-100 text-green-700 hover:bg-green-100" };
  const endDate = new Date(endDateString);
  const today = new Date();
  const diffTime = endDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return { label: "Completed", className: "bg-muted text-muted-foreground hover:bg-muted" };
  if (diffDays <= 180) return { label: "Ending Soon", className: "bg-yellow-100 text-yellow-700 hover:bg-yellow-100" };
  return { label: "Active", className: "bg-green-100 text-green-700 hover:bg-green-100" };
};

const FeaturedGrantsCard = ({
  data,
  showAll = false,
  isLoading,
  error,
  isHidden,
  onRetry,
  onSeeMore,
  seeMoreText = "View all",
}) => {
  return (
    <SectionCard
      title="Featured Grants"
      description="Top active grants by awarded amount"
      isLoading={isLoading}
      error={error}
      isHidden={isHidden}
      isEmpty={!data || data.length === 0}
      onRetry={onRetry}
      onSeeMore={onSeeMore}
      seeMoreText={seeMoreText}
    >
      <div className={`flex flex-col gap-4 ${showAll ? "max-h-[460px] overflow-y-auto pr-2" : ""}`}>
        {(() => {
          const uniqueGrants = data?.reduce((acc, current) => {
            const isDuplicate = acc.find(item => item.award_title === current.award_title && item.amount_awarded === current.amount_awarded);
            if (!isDuplicate) return [...acc, current];
            return acc;
          }, []) || [];
          
          const displayList = showAll ? uniqueGrants : uniqueGrants.slice(0, 5);
          return displayList.map((grant, idx) => {
            const status = getGrantStatus(grant.end_date);
            return (
              <div key={grant.id || idx} className="space-y-1.5 p-2 rounded-lg hover:bg-accent/30 transition-colors">
                <div className="flex justify-between items-start gap-3">
                  <span className="font-semibold text-sm line-clamp-1 flex-1" title={grant.award_title}>
                    {grant.award_title}
                  </span>
                  <div className="flex items-center gap-2 shrink-0">
                    {status.label !== "Active" && (
                      <Badge variant="secondary" className={`text-[9px] uppercase font-bold tracking-wider px-1.5 py-0 ${status.className}`}>
                        {status.label}
                      </Badge>
                    )}
                    <span className="font-bold text-sm whitespace-nowrap text-explore tabular-nums">
                      {formatCurrency(grant.amount_awarded)}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 text-[11px] text-muted-foreground truncate">
                  {grant.sponsors?.length > 0 && (
                    <div className="flex items-center gap-1 truncate max-w-[220px]">
                      <span className="capitalize truncate" title={grant.sponsors[0].name}>
                        {grant.sponsors[0].name?.toLowerCase()}
                      </span>
                      {grant.sponsors.length > 1 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="cursor-help underline decoration-dashed underline-offset-2 shrink-0">
                                +{grant.sponsors.length - 1} more
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="flex flex-col gap-1">
                                {grant.sponsors.slice(1).map(s => (
                                  <span key={s.id} className="capitalize">{s.name?.toLowerCase()}</span>
                                ))}
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  )}
                  {grant.sponsors?.length > 0 && grant.start_date && (
                    <span className="w-1 h-1 rounded-full bg-border shrink-0" />
                  )}
                  {(grant.start_date || grant.end_date) && (
                    <span className="shrink-0">
                      {grant.start_date ? formatDate(grant.start_date) : "N/A"} - {grant.end_date ? formatDate(grant.end_date) : "N/A"}
                    </span>
                  )}
                </div>
              </div>
            );
          });
        })()}
      </div>
    </SectionCard>
  );
};

export default FeaturedGrantsCard;
